import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import cl from 'classnames';

import { colorsV2 } from '../colors-v2';

interface ColoredConfig {
  barStyle?: 'colored';
}

interface StripeConfig {
  barStyle?: 'stripe';
  rotateStyle?: string; // Follow css styling. Eg. -39deg or 140deg
  gap?: number;
}

type Config = ColoredConfig | StripeConfig;

const getMoveStripes = (gap) => keyframes`
  from {
    background-position: 0 0;
  }
  to {
    background-position: ${3 * gap}px 0;
  }
`;

const Progress = styled.div<{ progressColor?: string; borderRadius?: number; config?: Config }>`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  transform-style: preserve-3d;
  transform-origin: left;
  background: ${(p) => p.progressColor};
  border-radius: ${(p) => p.borderRadius}px;
  transition: max-width 0.2s linear;

  &.colored {
    background: ${(p) => p.progressColor};
  }

  &.stripe {
    ${(p) => {
      const config = p.config as StripeConfig;
      const gap = config?.gap || 10;

      return css`
        background: repeating-linear-gradient(
          ${config?.rotateStyle || '140deg'},
          transparent,
          transparent ${gap}px,
          ${colorsV2.white10} ${gap}px,
          ${colorsV2.white10} ${2 * gap}px
        ),
        ${p.progressColor};

        &.animated {
          background-size: ${3 * gap}px 100%;
          animation: ${getMoveStripes(gap)} 0.5s linear infinite;
        }
      `;
    }}
  }
`;

const Bar = styled.div<{
  barColor?: string;
  height?: number;
  borderRadius?: number;
}>`
  position: relative;
  border-radius: ${(p) => p.borderRadius}px;
  overflow: hidden;
  transition: all 0.2s ease;
  background: ${(p) => p.barColor};

  min-width: 20px;
  height: ${(p) => p.height}px;
`;

const QuizNode = styled.div`
  width: 2px;
  height: 2px;
  background: ${colorsV2.yellow80};
  position: absolute;
  z-index: 10;
`;

interface ProgressBarProps {
  barStyle?: 'colored' | 'stripe';
  config?: Config;

  /*
   * percentage go from 0 to 100
   * */
  className?: string;
  percentage?: number;
  barColor?: string;
  progressColor?: string;
  height?: number;
  borderRadius?: number;
  quizNodes?: number[];

  onClick?: (e: any) => void;
  animated?: boolean;
}

export const ProgressBar = (props: ProgressBarProps) => {
  const {
    barStyle = 'colored',
    config,
    className,
    percentage = 0,
    barColor = colorsV2.green10,
    progressColor = colorsV2.gradientGreen,
    height = 12,
    borderRadius = 8,
    quizNodes = [],
    onClick,
    animated = false,
  } = props;

  return (
    <Bar
      className={cl('progress-bar', className)}
      barColor={barColor}
      height={height}
      borderRadius={borderRadius}
      onClick={onClick}
    >
      <Progress
        style={{ maxWidth: `${percentage}%` }}
        progressColor={progressColor}
        borderRadius={borderRadius}
        config={config}
        className={cl('progress', barStyle, { animated })}
      />
      {Boolean(quizNodes?.length) &&
        quizNodes.map((eachNodePercentage, idx) => (
          <QuizNode
            key={idx}
            style={{
              left: `${eachNodePercentage}%`,
            }}
          />
        ))}
    </Bar>
  );
};
